import React, { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import type { Product, ProductOptionValue } from '../types';

interface ProductCardProps {
  product: Product;
  onAddToCart: (
    product: Product,
    selectedOptions: Record<string, string>,
    optionsCost: number
  ) => void;
}

export function ProductCard({ product, onAddToCart }: ProductCardProps) {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(() => {
    return product.options.reduce((acc, option) => {
      acc[option.name] = option.values[0].value;
      return acc;
    }, {} as Record<string, string>);
  });

  const totalOptionsCost = useMemo(() => {
    return product.options.reduce((total, option) => {
      const selectedValue = selectedOptions[option.name];
      const optionValue = option.values.find(v => v.value === selectedValue);
      return total + (optionValue?.additionalCost || 0);
    }, 0);
  }, [selectedOptions, product.options]);

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionName]: value,
    }));
  };

  const totalPrice = product.price + totalOptionsCost;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-[1.02]">
      <img
        src={product.image}
        alt={product.name}
        className="w-full h-48 object-cover"
      />
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-800">{product.name}</h3>
        <p className="text-gray-600 mt-1 text-sm">{product.description}</p>

        {product.options.length > 0 && (
          <div className="mt-4 space-y-3">
            {product.options.map((option) => (
              <div key={option.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {option.name}
                </label>
                <select
                  value={selectedOptions[option.name]}
                  onChange={(e) => handleOptionChange(option.name, e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-teal-500 bg-gray-50 p-2 text-sm"
                >
                  {option.values.map((optionValue) => (
                    <option key={optionValue.value} value={optionValue.value}>
                      {optionValue.value}
                      {optionValue.additionalCost > 0 && ` (+£${optionValue.additionalCost.toFixed(2)})`}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div>
            <span className="text-xl font-bold text-gray-900">
              £{totalPrice.toFixed(2)}
            </span>
            {totalOptionsCost > 0 && (
              <span className="text-sm text-gray-500 block">
                Base: £{product.price.toFixed(2)}
              </span>
            )}
          </div>
          <button
            onClick={() => onAddToCart(product, selectedOptions, totalOptionsCost)}
            className="flex items-center gap-1 bg-[#6389af] text-white px-4 py-2 rounded-md hover:bg-[#557694] transition-colors"
          >
            <Plus size={18} />
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}